import { getCertificateStatus } from '@/features/classes/stores'
import type { Certificate, SessionAttendee, Student } from '@/features/classes/stores'
import { LANDING_URL } from '@/core/api/client'

import { fmtDate } from './formatters'

const UBSAFETY_LOGO_URL = `${window.location.origin}/images/logo/ub-logo-128.webp`

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/* ─────────────── Course accent colors (the only variation per course) ─────────────── */
const COURSE_ACCENTS: Record<string, string> = {
  'fall-protection':     '#1d4ed8', // blue
  'whmis':               '#16a34a', // green
  'forklift':            '#dc2626', // red
  'scissor-boom-lift':   '#f97316', // orange
  'telehandler':         '#d97706', // amber
  'bobcat-skid-steer':   '#7c3aed', // purple
  'respirator-fit-test': '#0891b2', // teal
}

function getCourseAccent(courseSlug: string): string {
  return COURSE_ACCENTS[courseSlug] || '#1d4ed8'
}

/* ─────────────── Course subtitles ─────────────── */
const COURSE_SUBTITLE: Record<string, string> = {
  'fall-protection':     'Fall Restraint and Fall Arrest',
  'whmis':               'Workplace Hazardous Materials Information System',
  'forklift':            'Counterbalanced Forklift Operation',
  'scissor-boom-lift':   'Boom Lift and Scissor Lift Operation',
  'telehandler':         'Telehandler Operation',
  'bobcat-skid-steer':   'Skid Steer and Bobcat Operation',
  'respirator-fit-test': 'Qualitative Fit Testing',
}

function getCourseSubtitle(courseSlug: string): string {
  return COURSE_SUBTITLE[courseSlug] || ''
}

/* ─────────────── Certificate HTML Generator ─────────────── */
export function getCertificateHTML(
  cert: Certificate,
  attendee: SessionAttendee & { student: Student },
  courseName: string,
  courseSlug: string = 'fall-protection',
  instructorName: string = 'Jorge Ruiz',
  courseColor?: string
): string {
  const status = getCertificateStatus(cert)
  const accent = courseColor ?? getCourseAccent(courseSlug)
  const subtitle = getCourseSubtitle(courseSlug)
  const verifyUrl = `${LANDING_URL}/verify/${encodeURIComponent(cert.verificationToken)}`
  const fullName = `${escapeHtml(attendee.student.firstName)} ${escapeHtml(attendee.student.lastName)}`.toUpperCase()

  const statusColor = status === 'valid' ? '#16a34a' : status === 'expired' ? '#d97706' : '#dc2626'
  const statusLabel = status === 'valid' ? 'VALID' : status === 'expired' ? 'EXPIRED' : 'REVOKED'

  // Inline SVG icons
  const icons = {
    calendar: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`,
    check: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>`,
    id: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M6 9h4"/><path d="M6 13h2"/><circle cx="14" cy="11" r="2"/><path d="M17 13v-1a2 2 0 0 0-2-2h0"/></svg>`,
    user: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`,
    shield: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="${statusColor}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`,
    checkmark: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`,
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Certificate – ${escapeHtml(cert.certificateNumber)}</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body {
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
      background: #e5e7eb;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 20px;
    }
    .cert {
      background: #ffffff;
      max-width: 1000px;
      width: 100%;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 10px 40px rgba(0,0,0,0.12);
    }

    /* ─── Header ─── */
    .header {
      background: #0b1120;
      padding: 28px 40px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
    }
    .header-accent {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 40%;
      height: 3px;
      background: ${accent};
    }
    .header-left {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .header-left .logo {
      width: 52px;
      height: 52px;
      object-fit: contain;
    }
    .header-left .brand-name {
      font-size: 16px;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: 0.04em;
      line-height: 1.2;
    }
    .header-left .brand-sub {
      font-size: 11px;
      font-weight: 600;
      color: ${accent};
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin-top: 2px;
    }
    .header-divider {
      width: 1px;
      height: 48px;
      background: rgba(255,255,255,0.15);
      margin: 0 20px;
    }
    .header-center {
      flex: 1;
      text-align: center;
    }
    .header-center .course-lower {
      font-size: 22px;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }
    .header-right {
      display: flex;
      align-items: center;
      gap: 10px;
      max-width: 220px;
    }
    .header-right .check-circle {
      width: 32px;
      height: 32px;
      border: 2px solid ${accent};
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .header-right .check-circle svg {
      width: 16px;
      height: 16px;
    }
    .header-right .verified-text {
      font-size: 10px;
      color: rgba(255,255,255,0.7);
      line-height: 1.4;
    }
    .header-right .verified-text strong {
      color: ${accent};
      font-weight: 700;
      font-size: 11px;
      display: block;
      letter-spacing: 0.05em;
    }

    /* ─── Body ─── */
    .body {
      padding: 40px 60px 32px;
      text-align: center;
    }
    .confirms {
      font-size: 12px;
      font-weight: 700;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.18em;
      margin-bottom: 14px;
    }
    .student-name {
      font-size: 48px;
      font-weight: 900;
      color: #0f172a;
      letter-spacing: 0.04em;
      line-height: 1.1;
    }
    .name-line {
      width: 60px;
      height: 2px;
      background: ${accent};
      margin: 14px auto 16px;
    }
    .completed {
      font-size: 15px;
      font-weight: 400;
      color: #374151;
      margin-bottom: 6px;
    }
    .course-name {
      font-size: 22px;
      font-weight: 800;
      color: ${accent};
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .course-sub {
      font-size: 14px;
      font-weight: 500;
      color: #6b7280;
      margin-top: 4px;
    }

    /* ─── Info Cards ─── */
    .info-cards {
      display: flex;
      justify-content: center;
      gap: 0;
      margin: 32px auto 0;
      max-width: 780px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      overflow: hidden;
    }
    .info-card {
      flex: 1;
      text-align: center;
      padding: 20px 12px;
      position: relative;
    }
    .info-card:not(:last-child)::after {
      content: '';
      position: absolute;
      right: 0;
      top: 20%;
      bottom: 20%;
      width: 1px;
      background: #e5e7eb;
    }
    .info-card .icon {
      margin-bottom: 8px;
      display: flex;
      justify-content: center;
    }
    .info-card .label {
      font-size: 9px;
      font-weight: 700;
      color: ${accent};
      text-transform: uppercase;
      letter-spacing: 0.12em;
      margin-bottom: 6px;
    }
    .info-card .value {
      font-size: 14px;
      font-weight: 700;
      color: #1f2937;
    }
    .info-card .value-mono {
      font-family: 'SF Mono', Monaco, monospace;
      font-size: 13px;
      font-weight: 700;
      color: #1f2937;
      letter-spacing: 0.02em;
    }
    .info-card .status-value {
      font-size: 14px;
      font-weight: 800;
      color: ${statusColor};
      letter-spacing: 0.04em;
    }

    /* ─── Footer ─── */
    .cert-footer {
      padding: 24px 60px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 32px;
    }
    .sig-block {
      text-align: center;
      min-width: 160px;
    }
    .sig-line {
      font-family: 'Brush Script MT', 'Segoe Script', cursive;
      font-size: 28px;
      color: #1f2937;
      padding-bottom: 6px;
      border-bottom: 1px solid #d1d5db;
      margin-bottom: 8px;
      line-height: 1;
    }
    .sig-name {
      font-size: 13px;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 3px;
    }
    .sig-label {
      font-size: 9px;
      font-weight: 700;
      color: ${accent};
      text-transform: uppercase;
      letter-spacing: 0.12em;
    }
    .seal-block {
      text-align: center;
    }
    .seal-block img {
      width: 90px;
      height: 90px;
      object-fit: contain;
    }
    .qr-block {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .qr-block img {
      width: 72px;
      height: 72px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 3px;
      background: #ffffff;
    }
    .qr-text {
      text-align: left;
    }
    .qr-text .scan-label {
      font-size: 11px;
      font-weight: 800;
      color: ${accent};
      text-transform: uppercase;
      letter-spacing: 0.06em;
      margin-bottom: 2px;
    }
    .qr-text .scan-url {
      font-size: 13px;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 4px;
    }
    .qr-text .scan-desc {
      font-size: 10px;
      font-weight: 500;
      color: #9ca3af;
      line-height: 1.4;
    }

    /* ─── Bottom Bar ─── */
    .bottom-bar {
      border-top: 2px solid ${accent};
      padding: 12px 40px;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }
    .bottom-bar span {
      font-size: 9px;
      font-weight: 600;
      color: #9ca3af;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }
    .bottom-bar .divider {
      width: 1px;
      height: 10px;
      background: #d1d5db;
    }

    @media print {
      body { background: white; padding: 0; }
      .cert { box-shadow: none; border-radius: 0; max-width: 100%; }
    }
    @media (max-width: 720px) {
      body { padding: 8px; }
      .header { flex-direction: column; gap: 16px; padding: 20px 24px; }
      .header-divider { display: none; }
      .header-center { order: -1; }
      .body { padding: 28px 24px; }
      .student-name { font-size: 32px; }
      .info-cards { flex-direction: column; border-radius: 8px; }
      .info-card:not(:last-child)::after { display: none; border-bottom: 1px solid #e5e7eb; }
      .cert-footer { flex-direction: column; gap: 24px; padding: 20px 24px; }
      .bottom-bar { padding: 10px 20px; }
    }
  </style>
</head>
<body>
  <div class="cert">
    <!-- Header -->
    <div class="header">
      <div class="header-accent"></div>
      <div class="header-left">
        <img class="logo" src="${UBSAFETY_LOGO_URL}" alt="UBSafety" />
        <div>
          <div class="brand-name">UBIQUITOUS<br/>SAFETY TRAINING</div>
          <div class="brand-sub">British Columbia Edition</div>
        </div>
      </div>
      <div class="header-divider"></div>
      <div class="header-center">
        <div class="course-lower">Certificate of Training</div>
      </div>
      <div class="header-divider"></div>
      <div class="header-right">
        <div class="check-circle">
          ${icons.checkmark}
        </div>
        <div class="verified-text">
          <strong>VERIFIED CERTIFICATE</strong>
          This certificate can be verified online for authenticity.
        </div>
      </div>
    </div>

    <!-- Body -->
    <div class="body">
      <div class="confirms">THIS CERTIFIES THAT</div>
      <div class="student-name">${escapeHtml(fullName)}</div>
      <div class="name-line"></div>
      <div class="completed">has successfully completed</div>
      <div class="course-name">${escapeHtml(courseName.toUpperCase())}</div>
      ${subtitle ? `<div class="course-sub">(${escapeHtml(subtitle)})</div>` : ''}

      <!-- Info Cards -->
      <div class="info-cards">
        <div class="info-card">
          <div class="icon">${icons.calendar}</div>
          <div class="label">Course Date</div>
          <div class="value">${escapeHtml(fmtDate(cert.issuedAt))}</div>
        </div>
        <div class="info-card">
          <div class="icon">${icons.check}</div>
          <div class="label">Expiry Date</div>
          <div class="value">${escapeHtml(fmtDate(cert.expiresAt))}</div>
        </div>
        <div class="info-card">
          <div class="icon">${icons.id}</div>
          <div class="label">Certificate ID</div>
          <div class="value-mono">${escapeHtml(cert.certificateNumber)}</div>
        </div>
        <div class="info-card">
          <div class="icon">${icons.user}</div>
          <div class="label">Trainer</div>
          <div class="value">${escapeHtml(instructorName)}</div>
        </div>
        <div class="info-card">
          <div class="icon">${icons.shield}</div>
          <div class="label">Status</div>
          <div class="status-value">${escapeHtml(statusLabel)}</div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="cert-footer">
      <div class="sig-block">
        <div class="sig-line">${escapeHtml(instructorName)}</div>
        <div class="sig-name">${escapeHtml(instructorName)}</div>
        <div class="sig-label">Trainer Signature</div>
      </div>
      <div class="seal-block">
        <img src="${UBSAFETY_LOGO_URL}" alt="UBSafety Seal" />
      </div>
      <div class="qr-block">
        <img src="https://api.qrserver.com/v1/create-qr-code/?size=144x144&data=${encodeURIComponent(verifyUrl)}" alt="QR" />
        <div class="qr-text">
          <div class="scan-label">Scan to Verify</div>
          <div class="scan-url">ubsafety.ca/verify</div>
          <div class="scan-desc">Or visit our website and enter<br/>the Certificate ID to verify.</div>
        </div>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="bottom-bar">
      <span>This certificate is only valid when verified online.</span>
      <div class="divider"></div>
      <span>Ubiquitous Safety Training Inc.</span>
      <div class="divider"></div>
      <span>778-788-0688</span>
      <div class="divider"></div>
      <span>info@ubsafety.ca</span>
      <div class="divider"></div>
      <span>ubsafety.ca</span>
    </div>
  </div>

  <script>window.onload = () => window.print()</script>
</body>
</html>`
}

export function openCertificatePDF(
  cert: Certificate,
  attendee: SessionAttendee & { student: Student },
  courseName: string,
  courseSlug?: string,
  instructorName?: string,
  courseColor?: string
) {
  const html = getCertificateHTML(cert, attendee, courseName, courseSlug, instructorName, courseColor)
  const blob = new Blob([html], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const win = window.open(url, '_blank')
  if (win) {
    win.onunload = () => URL.revokeObjectURL(url)
  }
}

/**
 * Generate certificate HTML for preview purposes using mock data.
 */
export function getCertificatePreviewHTML(
  courseName: string,
  courseSlug: string = 'fall-protection',
  courseColor?: string
): string {
  const today = new Date()
  const expiry = new Date(today)
  expiry.setFullYear(today.getFullYear() + 3)

  const mockCert: Certificate = {
    id: 'preview-cert-id',
    attendeeId: 'preview-attendee-id',
    certificateNumber: 'UBS-2026-99999',
    issuedAt: today.toISOString().split('T')[0],
    expiresAt: expiry.toISOString().split('T')[0],
    verificationToken: 'preview-token',
    revoked: false,
  }

  const mockAttendee: SessionAttendee & { student: Student } = {
    id: 'preview-attendee-id',
    sessionId: 'preview-session-id',
    studentId: 'preview-student-id',
    registeredAt: today.toISOString(),
    quizAnswers: {},
    quizScore: null,
    status: 'completed',
    source: 'manual',
    firstName: 'John',
    lastName: 'Doe',
    student: {
      id: 'preview-student-id',
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      phone: '555-123-4567',
      company: 'Example Corp',
    },
  }

  return getCertificateHTML(
    mockCert,
    mockAttendee,
    courseName,
    courseSlug,
    'Jorge Ruiz',
    courseColor
  )
}
